import {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../hooks/use-is-mounted";
import useCachedPriceLevelsDictionaryController from "../../../hooks/use-price-level-dictionary";
import Api from "../../../../core/services/api/api";
import {formatMoney} from "../../../../core/services/utils/utils";
import {CustomerAccountAndSearchesDetailsRecentSearchesDataGridCellKeys} from "../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../packages/data-grid";
import ProductInformationPopover from "../draggable-popover/product-information-popover";
import Grid2 from "@mui/material/Unstable_Grid2";
import SummaryInfoCard from "../summary-cards/summary-info-card";
import PartnerDataGrid from "../../../containers/data-grid";
import {Box, Typography} from "@mui/material";

const accountInfoSummaryBodyIds = {
    limitedCredit: 'limitedCredit',
    otherOrders: 'otherOrders',
    availableCredit: 'availableCredit',
    priceLevel: 'priceLevel',
    paymentTerms: 'paymentTerms',
    accountReceivable: 'accountReceivable',
}

const dataGridSaveConfig = {
    name: 'CustomerAccountAndSearchesDetailsDataGrid',
    version: 1,
}

const CustomerAccountingAndSearchesDetails = ({
                                                  customer,
                                                  showAccounting = false,
                                                  showSearchLogs = false,
                                              }) => {
    const [accountInformation, setAccountInformation] = useState(null);
    const [recentSearchLogs, setRecentSearchLogs] = useState([]);
    const [accountLoading, setAccountLoading] = useState(false);
    const [recentLoading, setRecentLoading] = useState(false);
    const isMounted = useIsMounted();
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();

    /**
     * Fetches the account information of the current customer from the server.
     */
    const getCustomerAccountInformation = useCallback(async () => {
        setAccountLoading(true);
        const [response, priceLevelDict] = await Promise.all([
            Api.getCustomerAccountInformation(customer?.id),
            getPriceLevelDictionary(),
        ]);
        if (!isMounted())
            return;
        setAccountLoading(false);
        if (response?.resultFlag) {
            setAccountInformation({
                ...response?.data,
                priceLevel: {
                    ...response?.data?.priceLevel,
                    title: priceLevelDict?.find(pl => pl.priceLevelId === response?.data?.priceLevel?.id)?.title ?? response?.data?.priceLevel?.title,
                }
            });
        }
    }, [customer?.id, getPriceLevelDictionary, isMounted])

    /**
     * Fetches the recent-search logs of the current customer from the server.
     */
    const getCustomerRecentSearches = useCallback(async () => {
        setRecentLoading(true);
        // TODO: change the api call to a new structure
        const response = await Api.getCustomerRecentSearches(customer?.id);
        if (!isMounted())
            return;
        setRecentLoading(false);
        if (response?.resultFlag) {
            setRecentSearchLogs(response?.data?.visits ?? [])
        }
    }, [customer?.id, isMounted])

    /**
     * With each change in the [id] of the given customer:
     * - if the customer [isFromSpire] then we manually set the account info and search logs
     * - else, we fetch the required information from the server
     */
    useEffect(() => {
        if (!!customer?.id) {
            setAccountLoading(showAccounting);
            setRecentLoading(showSearchLogs);
        }
    }, [customer?.id, showAccounting, showSearchLogs]);

    /**
     * With each change in the [id] of the given customer:
     * - if the customer [isFromSpire] then we manually set the account info and search logs
     * - else, we fetch the required information from the server
     */
    useEffect(() => {
        if (!customer?.id) {
            setAccountInformation(null);
            setRecentSearchLogs([]);
            setRecentLoading(false);
            setRecentLoading(false);
            return;
        }
        if (showAccounting) {
            void getCustomerAccountInformation();
        }
        if (showSearchLogs) {
            void getCustomerRecentSearches();
        }
    }, [customer?.id, getCustomerAccountInformation, getCustomerRecentSearches, showAccounting, showSearchLogs]);

    const accountSummaryInfo = useMemo(() => {
        return {
            header: {
                title: `Account Receivable of ${customer?.name ?? "Customer"}`,
                value: accountInformation?.accountReceivable,
                loadingValue: "Loading...",
                format: (val) => val === null || val === undefined ? "--" : formatMoney(val),
            },
            body: [
                {
                    id: accountInfoSummaryBodyIds.accountReceivable,
                    title: 'Account receivable',
                    value: accountInformation?.accountReceivable,
                    format: (val) => val === null || val === undefined ? "--" : formatMoney(val),
                },
                {
                    id: accountInfoSummaryBodyIds.limitedCredit,
                    title: 'Credit limit',
                    value: accountInformation?.creditLimit,
                    format: (val) => val === null || val === undefined ? "--" : formatMoney(val),
                },
                {
                    id: accountInfoSummaryBodyIds.otherOrders,
                    title: 'Other orders',
                    value: accountInformation?.otherOrders,
                    format: (val) => val === null || val === undefined ? "--" : formatMoney(val),
                },
                {
                    id: accountInfoSummaryBodyIds.availableCredit,
                    title: 'Available credit',
                    value: accountInformation?.availableCredit,
                    format: (val) => val === null || val === undefined ? "--" : formatMoney(val),
                },
                {
                    id: accountInfoSummaryBodyIds.priceLevel,
                    title: 'General Price level',
                    value: accountInformation?.priceLevel?.title,
                },
                {
                    id: accountInfoSummaryBodyIds.paymentTerms,
                    title: 'Payment terms',
                    value: accountInformation?.paymentTermsCode,
                },
            ],
        };
    }, [accountInformation?.accountReceivable, accountInformation?.availableCredit, accountInformation?.creditLimit, accountInformation?.otherOrders, accountInformation?.paymentTermsCode, accountInformation?.priceLevel?.title, customer?.name])

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Search date",
                name: CustomerAccountAndSearchesDetailsRecentSearchesDataGridCellKeys.date,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                sortable: false,
            },
            {
                title: "Searched by",
                name: CustomerAccountAndSearchesDetailsRecentSearchesDataGridCellKeys.searchedBy,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Search type",
                name: CustomerAccountAndSearchesDetailsRecentSearchesDataGridCellKeys.searchType,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Clicked part",
                name: CustomerAccountAndSearchesDetailsRecentSearchesDataGridCellKeys.clickedPart,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
        ], [])

    const [dataGridColumns] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() =>
        recentSearchLogs.map(log => {
            return ({
                key: log.dateTime,
                data: log,
                cells: {
                    [CustomerAccountAndSearchesDetailsRecentSearchesDataGridCellKeys.date]: log.dateTime,
                    [CustomerAccountAndSearchesDetailsRecentSearchesDataGridCellKeys.searchedBy]:
                        (log.userSearched ?? false)
                            ? `${log.user?.firstName ?? '--'} ${log.user?.lastName ?? '--'}`
                            : `${log.operator?.firstName ?? '--'} ${log.operator?.lastName ?? '--'}`,
                    [CustomerAccountAndSearchesDetailsRecentSearchesDataGridCellKeys.searchType]: log.searchTypeName,
                    [CustomerAccountAndSearchesDetailsRecentSearchesDataGridCellKeys.clickedPart]: log.clickedPart?.partNumber
                        ? <ProductInformationPopover partNo={log.clickedPart?.partNumber}>
                            {log.clickedPart?.partNumber ?? ''}
                        </ProductInformationPopover>
                        : '--',
                },
            });
        }), [recentSearchLogs])

    return (
        <>
            <Grid2 container sx={{width: '100%'}}>
                {
                    showAccounting &&
                    <Grid2 xs={12} lg={showSearchLogs ? 5 : 12}>
                        <SummaryInfoCard
                            header={accountSummaryInfo.header}
                            body={accountSummaryInfo.body}
                            loading={accountLoading}
                            loadingCount={accountSummaryInfo.body.length}
                        />
                    </Grid2>
                }
                {
                    showSearchLogs &&
                    <Grid2 xs={12} lg={showAccounting ? 7 : 12}>
                        <Box sx={{display: 'flex', flexDirection: 'column'}}>
                            <Typography variant={'body1'} fontWeight={600}>
                                Recent Search:
                            </Typography>
                            <PartnerDataGrid
                                {...dataGridSaveConfig}
                                initialLayoutState={{columns: columns}}
                                columns={dataGridColumns}
                                rows={dataGridRows}
                                hideToolbar
                                hideFooter
                                classNames={{toolbar: "pt-0"}}
                                visibleRows={5}
                                state={{loading: {state: recentLoading}}}
                            />
                        </Box>
                    </Grid2>
                }
            </Grid2>
        </>
    );
}

export default CustomerAccountingAndSearchesDetails;
